import React, { Fragment, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { HiOutlineSparkles, HiOutlineX } from "react-icons/hi";
import CustomButton from "components/common/Button/CustomButton";
import {
  releaseBodyScrollLockAfterTransition,
  useBodyScrollLock,
} from "../../TimeSheetDetailsSection/ai/utils/bodyScrollLock";

const SCROLL_ALLOW = "[data-bulk-analyse-scroll]";

export function BulkAnalyseModalChrome({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  wide = false,
  tallHeader = false,
  fillHeight = true,
  panelClassName = "",
}) {
  useBodyScrollLock(isOpen, { nestedScrollAllowSelector: SCROLL_ALLOW });

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  return (
    <Transition show={isOpen} as={Fragment} afterLeave={releaseBodyScrollLockAfterTransition}>
      <Dialog as="div" className="relative z-[10060]" open={isOpen} onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/50" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-hidden p-3 sm:p-4 lg:p-6">
          <div className="flex h-full min-h-0 items-center justify-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2"
              enterTo="opacity-100 translate-y-0"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-2"
            >
              <Dialog.Panel
                data-bulk-analyse-panel
                data-anchored-menu-boundary
                className={`relative flex max-h-full min-h-0 w-full min-w-0 transform flex-col overflow-hidden bg-white text-left shadow-2xl ${
                  fillHeight ? "h-full" : "h-auto"
                } ${
                  panelClassName || (wide ? "max-w-[1298px] rounded-2xl" : "max-w-lg rounded-2xl")
                }`}
              >
                <div className={`flex shrink-0 items-center justify-between gap-3 bg-gradient text-white ${tallHeader ? "h-[76px] px-6" : "h-16 px-4 sm:px-6"}`}>
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-white/15">
                      <HiOutlineSparkles className="h-[18px] w-[18px]" />
                    </span>
                    <div className={tallHeader ? "flex min-w-0 flex-col gap-0.5" : "min-w-0"}>
                      <Dialog.Title className={`truncate font-semibold ${tallHeader ? "text-[18px] leading-none" : "text-lg"}`}>
                        {title}
                      </Dialog.Title>
                      {subtitle ? (
                        <p className={`truncate font-medium text-[#DBEAFE] ${tallHeader ? "text-xs leading-none" : "text-xs"}`}>
                          {subtitle}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-white/10 text-white"
                    aria-label="Close"
                  >
                    <HiOutlineX className="h-4 w-4" />
                  </button>
                </div>
                <div
                  data-bulk-analyse-scroll
                  className={`flex min-h-0 flex-col ${fillHeight ? "flex-1 overflow-hidden" : "overflow-y-auto"}`}
                >
                  {children}
                </div>
                {footer}
                <div
                  data-bulk-analyse-menu-root
                  className="pointer-events-none absolute inset-0 z-30"
                />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export function BulkAnalyseFooter({ children, tall = false }) {
  return (
    <div className={`flex shrink-0 border-t border-[#E5E7EB] bg-[#F9FAFB] ${tall ? "flex-col gap-3 px-4 py-3 sm:px-6 lg:h-[72px] lg:flex-row lg:items-center lg:justify-between lg:gap-4 lg:py-0" : "flex-col gap-3 px-4 py-3 sm:px-6 lg:h-16 lg:flex-row lg:items-center lg:justify-between lg:gap-4 lg:py-0"}`}>
      {children}
    </div>
  );
}

export function BulkSecondaryButton({ children, onClick, disabled, className = "" }) {
  return (
    <CustomButton
      type="button"
      variant="secondary"
      label={children}
      clickHandler={onClick}
      isDisabled={disabled}
      cssClass={`h-[38px] py-0 !border !shadow-none focus:!ring-0 focus:!ring-offset-0 disabled:cursor-not-allowed disabled:opacity-40 ${className}`}
    />
  );
}

export function BulkPrimaryButton({ children, onClick, disabled, className = "", icon = null }) {
  return (
    <CustomButton
      type="button"
      variant="primary"
      label={children}
      Icon={icon}
      clickHandler={onClick}
      isDisabled={disabled}
      cssClass={`h-[38px] py-0 !gap-x-2 disabled:cursor-not-allowed disabled:opacity-40 ${className}`}
    />
  );
}
